<template>
  <section
    :id="'pitchtype-' + pitchtype.id"
    data-test="pitch-type-card"
    class="pitchtype"
    :class="{
      unavailable: !isAvailable,
      desktop: $isDesktop,
      en: $i18n.locale.split('-')[0] === 'en',
    }"
  >
    <nuxt-link :to="{ query: urlQuery }" class="pt-wrapper">
      <template v-if="$isDesktop">
        <pitch-type-image
          v-if="pitchTypePrimaryPhoto"
          :lazy="pitchtypeIndex != 0"
          :pitch-type-primary-photo="pitchTypePrimaryPhoto"
          :photo-count="pitchtype.photos.length"
          @click.prevent.stop="openPTGallery()"
        />
        <div v-else class="pt-img-placeholder" />
      </template>

      <header class="pt-header">
        <template v-if="!$isDesktop">
          <div class="top-row">
            <pu-category-icons
              :categories="categories"
              class="pitchtype-categories horizontal-spacing"
            />
            <span v-if="pitchtype.rating" class="pitch-rating">
              <img
                src="@/assets/images/user-reviews.svg"
                class="pitch-rating-icon"
              />
              {{ formatRating(Number(pitchtype.rating)) }}
            </span>
            <slot name="status-msg" />
          </div>
          <h3 class="pitchtype-name">
            {{ pitchtype.name }}
          </h3>
        </template>

        <pitch-type-summary
          :party="party"
          :pitchtype="pitchtype"
          :pitchtype-index="pitchtypeIndex"
          :show-pitchtype-details="false"
          :is-over-capacity="isOverCapacity"
          :is-available="isAvailable"
        >
          <template #pitch-type-image>
            <pitch-type-image
              v-if="pitchTypePrimaryPhoto && !$isDesktop"
              :lazy="pitchtypeIndex != 0"
              :pitch-type-primary-photo="pitchTypePrimaryPhoto"
              :photo-count="pitchtype.photos.length"
              @click.prevent.stop="openPTGallery()"
            />
          </template>
          <template v-if="!$isDesktop" #card-rhs>
            <section class="pt-body">
              <div class="mini-calendar-wrapper">
                <div
                  class="summary-card-row"
                  :class="{ 'action-btn': isAvailable }"
                >
                  <div class="calendar-block">
                    <translate class="calendar-title"> Arrival days </translate>
                    <client-only>
                      <pitchtype-mini-availability-calendar-with-availability
                        v-if="shouldShowMiniCal"
                        :stay-dates="datesRange"
                        :campsite-slug="campsiteSlug"
                        :pitchtype-id="pitchtype.id"
                        :months-to-show="2"
                        :has-navigation="false"
                        month-format="short"
                        :calendar-start-date="miniCalendarStartDate"
                      />
                    </client-only>
                  </div>
                  <div class="card-price-and-action">
                    <!-- holds price or 'No availability' in PT card -->
                    <slot name="state" :pitchtype="pitchtype" />
                    <div
                      v-if="isAvailable && availableToSearch"
                      class="view-prices"
                    >
                      <translate>View prices & book</translate>
                      <img
                        src="@/assets/images/forward-rounded.svg"
                        width="16"
                        height="16"
                        alt=""
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </template>
          <template #state>
            <!-- holds price or 'No availability' in PT detail -->
            <slot name="state" />
          </template>
          <template v-if="$slots['status-msg']" #status-msg>
            <slot name="status-msg" />
          </template>
          <template v-if="$isDesktop" #pitch-type-attributes>
            <pitch-type-attributes
              :pitchtype="pitchtype"
              :campsite-super-facilities="campsiteSuperFacilities"
            />
          </template>
        </pitch-type-summary>
      </header>

      <pitch-type-seo-card :pitchtype="pitchtype" :party="party" />

      <section v-if="$isDesktop" class="pt-body">
        <div class="mini-calendar-wrapper">
          <div class="summary-card-row" :class="{ 'action-btn': isAvailable }">
            <div class="calendar-block">
              <translate class="calendar-title"> Arrival days </translate>
              <client-only>
                <pitchtype-mini-availability-calendar-with-availability
                  v-if="shouldShowMiniCal"
                  :stay-dates="datesRange"
                  :campsite-slug="campsiteSlug"
                  :pitchtype-id="pitchtype.id"
                  :months-to-show="2"
                  :has-navigation="false"
                  month-format="short"
                  :calendar-start-date="miniCalendarStartDate"
                />
              </client-only>
            </div>
            <div class="card-price-and-action">
              <!-- holds price or 'No availability' in PT card -->
              <slot name="state" :pitchtype="pitchtype" />
            </div>
            <div v-if="isAvailable && availableToSearch" class="view-prices">
              <translate>View prices & book</translate>
              <img
                src="@/assets/images/forward-rounded.svg"
                width="16"
                height="16"
                alt=""
              />
            </div>
          </div>
        </div>
      </section>
    </nuxt-link>

    <lazy-pitch-type-gallery-modal
      v-if="galleryOpen && pitchtype.photos.length"
      :photos="pitchtype.photos"
      :fullscreen-modal-hash="`pitch-${pitchtype.id}-gallery`"
      :teleport="true"
    />
  </section>
</template>

<script lang="ts">
import { puNumber } from '~/lang/formatters/puNumber'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'

import { CampsiteFacility, PitchType } from '../../../campsite-details/types'
import { Party } from '../../types'
import { getPriceParamsFromQuery } from '~/utility/getPriceParamsFromQuery'
import { combineCategories } from '~/utility/pitchtype'

@Component({})
class BasePitchTypeCard extends Vue {
  @Prop()
  party: Party

  @Prop({ default: () => ({ start: null, end: null }) })
  datesRange: { start?: string; end?: string }

  @Prop()
  pitchtype: PitchType

  @Prop()
  campsiteSuperFacilities: CampsiteFacility[]

  @Prop()
  isOverCapacity: boolean

  @Prop()
  isAvailable: boolean

  @Prop()
  availableToSearch: boolean

  @Prop()
  campsiteSlug: string

  @Prop({ default: null })
  pitchtypeIndex: number | null

  @Prop()
  calendarStartDate: string

  galleryOpen = false
  shouldShowMiniCal = false

  get ptGalleryHash() {
    return `#pitch-${this.pitchtype.id}-gallery`
  }

  openPTGallery() {
    void this.$router.push({
      hash: this.ptGalleryHash,
      query: this.$router.currentRoute.value.query,
    })
    this.galleryOpen = true
  }

  @Watch('$route')
  onRouteChange() {
    if (this.$route.hash !== this.ptGalleryHash) {
      this.galleryOpen = false
    }
  }

  @Watch('$route.query.pitchtype', { immediate: true })
  datesRangeChanged(newVal) {
    if (newVal === undefined) {
      this.shouldShowMiniCal = true
    }
  }

  get categories() {
    try {
      return combineCategories(
        this.pitchtype.categories,
        this.pitchtype.negativeCategories,
      )
    } catch (e) {
      console.error(e)
      return []
    }
  }

  get urlQuery() {
    return {
      ...getPriceParamsFromQuery(this.$route.query),
      // facet should be preserved from getPriceParamsFromQuery
      pitchtype: this.pitchtype.id,
      sa: undefined,
      ved: undefined,
      review: undefined,
    }
  }

  get pitchTypePrimaryPhoto() {
    return this.pitchtype.photos[0]
  }

  get miniCalendarStartDate() {
    let startDate = new Date(this.calendarStartDate)
    // We only need the month so set the date
    // to middle of month to avoid skipping two months
    // if calendarStartDate is a month with more days
    // than month moved to.
    startDate.setUTCDate(10)
    startDate.setUTCHours(0, 0, 0, 0)
    const prevMonth = new Date()
    prevMonth.setUTCHours(0, 0, 0, 0)
    prevMonth.setUTCDate(10)
    prevMonth.setUTCMonth(startDate.getUTCMonth() - 1)

    const currentMonth = new Date()
    currentMonth.setUTCHours(0, 0, 0, 0)
    currentMonth.setUTCDate(10) // Fix: use setUTCDate instead of setDate

    if (prevMonth.getTime() >= currentMonth.getTime()) {
      startDate = prevMonth
    }

    return startDate.toISOString().split('T')[0]
  }

  formatRating(rating: number) {
    return puNumber(
      rating,
      this.$i18n.locale,
      'maximumFractionDigits:1 minimumFractionDigits:0',
    )
  }
}

export default toNative(BasePitchTypeCard)
</script>
<style scoped>
.pitchtype {
  background: white;
  border-radius: 0.4em;
  box-shadow: 0 0.25em var(--colour-very-dark-green);
}

.pitchtype:active {
  transform: scale(1.03);
  filter: brightness(0.8) drop-shadow(0 0.2em 0.3em #394a5a);
}

.pt-wrapper {
  display: block;
  padding: 0.45em 0.7em 0.6em;
  text-decoration: none;
}

.top-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.summary-card-row {
  display: grid;
  grid-template-rows: repeat(2, auto);
  grid-template-columns: auto;
  min-height: 6em;
  align-content: space-between;
  justify-items: end;
}

.pitchtype-name {
  color: var(--colour-dark-blue);
  font-size: 0.9em;
  font-weight: 600;
  line-height: 1.1;
  margin: 0.3em 0 0.55em 0;
}

.pitchtype-categories.category-icons {
  align-items: flex-end;
  display: flex;
  flex-shrink: 0;
  justify-content: flex-start;
}

.pitchtype-categories.category-icons :deep(img) {
  height: 0.95em;
}

.pitch-rating {
  margin: 0 auto 0 0.75em;
  font-size: var(--small);
  color: var(--colour-dark-blue);
  font-weight: 500;
  display: flex;
  gap: 0.15em;
  align-items: center;
  justify-content: start;
  white-space: nowrap;
}

.pitch-rating-icon {
  width: auto;
  height: 1.2em;
  position: relative;
  top: 1px;
}

.mini-calendar-wrapper {
  margin-top: -0.1em;
}

.calendar-block {
  flex-shrink: 0;
  display: flex;
  align-items: start;
  justify-content: end;
  column-gap: 0.35em;
}

.card-price-and-action {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  margin: 0.3em 0 0 auto;
}

.card-price-and-action :deep(.price) {
  margin: 0 0 0.5em 0;
  line-height: 1.1;
}

.card-price-and-action :deep(.pricing-period) {
  line-height: 0.75;
}

.view-prices {
  line-height: 1;
  align-items: center;
  background-color: var(--colour-mid-green);
  border-radius: 1em;
  color: white;
  display: flex;
  font-size: var(--small);
  justify-items: center;
  margin-right: -0.2em;
  padding: 0.4em 0.6em;
}

.view-prices img {
  display: flex;
  flex-shrink: 0;
  align-self: center;
  width: 0.9em;
  height: 0.9em;
  margin: 0 -0.2em 0 0.1em;
  opacity: 1;
}

.pt-header {
  height: auto;
  position: relative;
}

.calendar-title {
  color: #bbb;
  display: block;
  font-size: var(--tiny);
  font-style: italic;
  font-weight: normal;
  line-height: 0.9;
  margin: 0;
  padding-top: 13px;
  text-align: right;
  word-break: break-word;
  word-wrap: break-word;
  hyphens: auto;
}

.pitchtype.en .calendar-title {
  width: min-content;
  word-break: keep-all;
  word-wrap: normal;
}

/* desktop & tablet */
.pitchtype.desktop {
  height: 100%;
  border-radius: 0.6em;
  box-shadow: 0 0.3em var(--colour-very-dark-green);
}

.desktop .pt-wrapper {
  --pt-cards-desktop-multiplier: 1.3em;
  column-gap: 2em;
  display: grid;
  grid-template-columns: 264px 1fr auto;
  grid-template-rows: auto;
  height: 100%;
  padding: 1.2em;
  text-decoration: none;
}

.desktop .pt-header {
  height: auto;
  position: relative;
  grid-row: 1 / 2;
  grid-column: 2 / 3;
}

.desktop .pt-header :deep(.pt-details) {
  font-size: var(--pt-cards-desktop-multiplier);
  margin: 0;
}

.desktop .pt-img-placeholder {
  border-radius: 0.4em;
  width: 264px;
  aspect-ratio: 4/3;
  background: var(--colour-page-background);
}

.desktop .mini-calendar-wrapper {
  position: relative;
  height: 100%;
}

.desktop .calendar-block {
  align-items: end;
}

.desktop .summary-card-row {
  grid-template-rows: auto auto 1fr;
  height: 100%;
}

.desktop .summary-card-row.action-btn {
  margin-bottom: 3em;
}

.desktop .calendar-block {
  flex-shrink: 0;
  grid-row: 2 / 3;
  grid-column: 1 / 2;
  text-align: right;
  font-size: var(--pt-cards-desktop-multiplier);
  min-height: 3.6em;
  margin: 0;
  flex-direction: column;
}

.desktop .calendar-block :deep(.calendar) {
  align-items: flex-start;
  justify-content: flex-end;
}

.desktop .calendar-block :deep(.calendar .day) {
  width: 8px;
  height: 8px;
}

.desktop .calendar-title {
  max-width: none;
  color: var(--colour-text-light);
  font-style: normal;
  padding-top: 0;
  line-height: 1.3;
}

.desktop .pitchtype.en .calendar-title {
  width: max-content;
}

.desktop .card-price-and-action {
  display: block;
  margin: 0;
  grid-row: 1 / 2;
  grid-column: 1 / 2;
  font-size: var(--pt-cards-desktop-multiplier);
}

.desktop .view-prices {
  margin: auto 0 0 0;
  font-size: 1.125em;
}

/* small tablets */
@media (max-width: 850px) {
  .desktop .pt-wrapper {
    grid-template-columns: 180px auto auto;
  }

  .desktop .pt-img-placeholder {
    width: 180px;
  }
}

/* large desktop */
@media only screen and (min-width: 92em) {
  .desktop .pt-wrapper {
    padding: 1em;
    column-gap: 1em;
    grid-template-columns: 180px auto auto;
  }

  .desktop .pt-img-placeholder {
    width: 180px;
  }
}
</style>
